import React, { useContext, useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Avatar,
  Heading,
  Text,
  VStack,
  Icon,
  Button,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Divider,
  Spinner,
  Badge,
  HStack,
  Input,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faBuilding,
  faHatCowboy,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaBirthdayCake,
  FaUserTie,
  FaIdCard,
} from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import ApiService from "../services/ApiService";
import { resolveProfileImageSrc, compressProfileImage } from "../utils/profileImage";

const formatDate = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("th-TH", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatAddress = (data, prefix = "") => {
  if (!data) return "-";
  const parts = [
    data[`${prefix}AddressNo`] || data.AddressNo,
    data[`${prefix}AddressMoo`] || data.AddressMoo
      ? `หมู่ ${data[`${prefix}AddressMoo`] || data.AddressMoo}`
      : "",
    data[`${prefix}AddressRoad`] || data.AddressRoad,
    data[`${prefix}AddressSubdistrict`] || data.AddressSubdistrict,
    data[`${prefix}AddressDistrict`] || data.AddressDistrict,
    data[`${prefix}AddressProvince`] || data.AddressProvince,
    data[`${prefix}AddressZipcode`] || data.AddressZipcode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "-";
};

const InfoRow = ({ icon, label, value }) => (
  <Flex align="center">
    <Icon as={icon} color="gray.500" mr={4} />
    <Box>
      <Text fontSize="sm" color="gray.600">
        {label}
      </Text>
      <Text fontWeight="medium">{value || "-"}</Text>
    </Box>
  </Flex>
);

const livestockTypes = [
  { key: "BeefCattle", label: "โคเนื้อ" },
  { key: "DairyCattle", label: "โคนม" },
  { key: "Buffalo", label: "กระบือ" },
  { key: "Swine", label: "สุกร" },
  { key: "Chicken", label: "ไก่" },
  { key: "Duck", label: "เป็ด" },
  { key: "Goat", label: "แพะ" },
  { key: "Sheep", label: "แกะ" },
  { key: "Quail", label: "นกกระทา" },
  { key: "Dog", label: "สุนัข" },
  { key: "Cat", label: "แมว" },
  { key: "Other", label: "สัตว์อื่นๆ" },
];

const defaultLivestock = livestockTypes.reduce(
  (acc, item) => ({ ...acc, [item.key]: 0 }),
  {}
);

const FarmerProfile = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { profile, sessionId } = useContext(AuthContext) || {};
  const [farmerData, setFarmerData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingProfileImage, setPendingProfileImage] = useState(null);
  const [isSavingImage, setIsSavingImage] = useState(false);

  const fetchFarmerData = useCallback(async () => {
    if (!profile?.FarmerID) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await ApiService({
        url: `/getFarmerProfileByID/${profile.FarmerID}`,
        method: "GET",
        sessionId,
      });

      const data = response.data;
      setFarmerData({
        ...data,
        Livestock: { ...defaultLivestock, ...(data.Livestock || {}) },
        FarmAddress: data.FarmAddress || {},
        Contacts: data.Contacts || [],
      });
      setPendingProfileImage(null);
    } catch (error) {
      console.error("Failed to fetch farmer data", error);
    } finally {
      setIsLoading(false);
    }
  }, [profile?.FarmerID, sessionId]);

  useEffect(() => {
    fetchFarmerData();
  }, [fetchFarmerData]);

  const { getInputProps, open } = useDropzone({
    accept: { "image/*": [] },
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "ไฟล์รูปมีขนาดใหญ่เกินไป",
          description: "กรุณาเลือกรูปที่มีขนาดไม่เกิน 5 MB",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      try {
        const compressedImage = await compressProfileImage(file);
        setPendingProfileImage(compressedImage);
      } catch {
        toast({
          title: "ไม่สามารถประมวลผลรูปภาพได้",
          description: "กรุณาเลือกไฟล์รูปภาพอื่น",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    },
  });

  const handleSaveProfileImage = async () => {
    if (!pendingProfileImage || !profile?.FarmerID) return;

    setIsSavingImage(true);
    try {
      const response = await ApiService({
        method: "POST",
        url: "/updateFarmerProfileImage",
        data: {
          FarmerID: profile.FarmerID,
          ProfileImage: pendingProfileImage,
        },
        sessionId,
      });

      if (response.result !== "success") {
        throw new Error(response.error || "ไม่สามารถบันทึกรูปโปรไฟล์ได้");
      }

      toast({
        title: "บันทึกรูปโปรไฟล์สำเร็จ",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      await fetchFarmerData();
    } catch (error) {
      toast({
        title: error.message || "ไม่สามารถบันทึกรูปโปรไฟล์ได้",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSavingImage(false);
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="80vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (!farmerData) {
    return (
      <Box p={8} textAlign="center">
        <Text>ไม่พบข้อมูลเกษตรกร</Text>
        <Button mt={4} onClick={() => navigate(-1)}>
          ย้อนกลับ
        </Button>
      </Box>
    );
  }

  const isCorporate = farmerData.FarmerType === "นิติบุคคล";
  const displayName = isCorporate
    ? farmerData.FirstName
    : `${farmerData.FirstName || ""} ${farmerData.LastName || ""}`.trim();
  const profileImageSrc = resolveProfileImageSrc(
    pendingProfileImage || farmerData.ProfileImage
  );
  const livestockWithCount = livestockTypes.filter(
    (item) => (farmerData.Livestock?.[item.key] || 0) > 0
  );

  return (
    <Box p={{ base: 4, md: 8 }} bg="gray.50" minH="100vh">
      <Flex
        direction={{ base: "column", lg: "row" }}
        gap={8}
        maxW="1200px"
        mx="auto"
      >
        <VStack
          as={Card}
          p={8}
          spacing={4}
          align="center"
          w={{ base: "100%", lg: "350px" }}
        >
          <Box position="relative">
            <Input {...getInputProps()} />
            <Avatar
              size="2xl"
              src={profileImageSrc}
              name={!profileImageSrc ? displayName : undefined}
              icon={<FontAwesomeIcon icon={faUser} size="2x" />}
            />
          </Box>
          <Button
            size="sm"
            colorScheme="orange"
            variant="outline"
            onClick={open}
          >
            เปลี่ยนรูปโปรไฟล์
          </Button>
          {pendingProfileImage && (
            <HStack spacing={2}>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={handleSaveProfileImage}
                isLoading={isSavingImage}
                loadingText="กำลังบันทึก..."
              >
                บันทึกรูป
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPendingProfileImage(null)}
                isDisabled={isSavingImage}
              >
                ยกเลิก
              </Button>
            </HStack>
          )}
          <Text fontSize="xs" color="gray.500" textAlign="center">
            รองรับไฟล์รูปภาพ JPG, PNG
          </Text>
          <VStack spacing={1} align="center">
            <Text fontSize="lg" fontWeight="semibold" textAlign="center">
              {displayName || "-"}
            </Text>
            <HStack spacing={2}>
              <FontAwesomeIcon icon={isCorporate ? faBuilding : faUser} />
              <Text fontSize="md" color="gray.500">
                {farmerData.FarmerType || "บุคคลธรรมดา"}
              </Text>
            </HStack>
            {isCorporate && farmerData.CorporateType && (
              <Text fontSize="sm" color="gray.500" textAlign="center">
                {farmerData.CorporateType}
                {farmerData.CompanyBranch ? ` (${farmerData.CompanyBranch})` : ""}
              </Text>
            )}
          </VStack>

          <VStack spacing={3} w="full">
            <Button
              colorScheme="blue"
              w="full"
              onClick={() => navigate("/farmerprofile/edit")}
            >
              แก้ไขข้อมูล
            </Button>
            <Button
              colorScheme="teal"
              variant="outline"
              w="full"
              onClick={() => navigate("/farmerprofile/changepassword")}
            >
              เปลี่ยนแปลงรหัสผ่าน
            </Button>
            <Button
              colorScheme="gray"
              variant="outline"
              w="full"
              onClick={() => navigate(-1)}
            >
              ย้อนกลับ
            </Button>
          </VStack>
        </VStack>

        <VStack flex="1" spacing={6} align="stretch">
          <Card>
            <CardHeader>
              <Heading size="md">ข้อมูลทั่วไป</Heading>
            </CardHeader>
            <Divider />
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <InfoRow
                  icon={FaIdCard}
                  label={isCorporate ? "เลขทะเบียนนิติบุคคล" : "เลขบัตรประชาชน"}
                  value={farmerData.IDCard}
                />
                {!isCorporate && (
                  <>
                    <InfoRow
                      icon={FaUserTie}
                      label="เพศ"
                      value={
                        farmerData.Gender === "M"
                          ? "ชาย"
                          : farmerData.Gender === "F"
                          ? "หญิง"
                          : "-"
                      }
                    />
                    <InfoRow
                      icon={FaBirthdayCake}
                      label="วันเกิด"
                      value={formatDate(farmerData.BirthDate)}
                    />
                  </>
                )}
              </SimpleGrid>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Heading size="md">ข้อมูลการติดต่อ</Heading>
            </CardHeader>
            <Divider />
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <InfoRow icon={FaEnvelope} label="อีเมล" value={farmerData.Email} />
                <InfoRow icon={FaPhone} label="เบอร์โทรศัพท์" value={farmerData.Phone} />
                <InfoRow
                  icon={FaMapMarkerAlt}
                  label="ที่อยู่"
                  value={formatAddress(farmerData)}
                />
              </SimpleGrid>
            </CardBody>
          </Card>

          {isCorporate && farmerData.Contacts?.length > 0 && (
            <Card>
              <CardHeader>
                <Heading size="md">
                  <FontAwesomeIcon icon={faUserTie} style={{ marginRight: 8 }} />
                  ผู้ติดต่อประสานงาน
                </Heading>
              </CardHeader>
              <Divider />
              <CardBody>
                <VStack align="stretch" spacing={4}>
                  {farmerData.Contacts.map((contact) => (
                    <Box
                      key={contact.id || contact.ContactID}
                      p={4}
                      borderWidth={1}
                      borderRadius="md"
                      borderColor={contact.IsPrimary ? "orange.200" : "gray.200"}
                      bg={contact.IsPrimary ? "orange.50" : "white"}
                    >
                      <HStack mb={2}>
                        <Badge colorScheme={contact.IsPrimary ? "orange" : "gray"}>
                          {contact.IsPrimary ? "ผู้ติดต่อหลัก" : "ผู้ติดต่อ"}
                        </Badge>
                      </HStack>
                      <Text fontWeight="medium">{contact.ContactName || "-"}</Text>
                      <Text fontSize="sm" color="gray.600">
                        โทร: {contact.ContactPhone || "-"}
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        อีเมล: {contact.ContactEmail || "-"}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader>
              <Heading size="md">
                <FontAwesomeIcon icon={faHatCowboy} style={{ marginRight: 8 }} />
                ข้อมูลปศุสัตว์
              </Heading>
            </CardHeader>
            <Divider />
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <InfoRow
                  icon={FaMapMarkerAlt}
                  label="ชื่อฟาร์ม / ชื่อบริษัท"
                  value={farmerData.FarmName}
                />
                <InfoRow
                  icon={FaMapMarkerAlt}
                  label="ที่ตั้งฟาร์ม"
                  value={formatAddress(farmerData.FarmAddress)}
                />
                {livestockWithCount.length > 0 ? (
                  <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4} pt={2}>
                    {livestockWithCount.map((item) => (
                      <Box key={item.key} p={3} bg="green.50" borderRadius="md">
                        <Text fontSize="sm" color="gray.600">
                          {item.label}
                        </Text>
                        <Text fontWeight="bold">
                          {farmerData.Livestock[item.key]} ตัว
                        </Text>
                      </Box>
                    ))}
                  </SimpleGrid>
                ) : (
                  <Text color="gray.500">ไม่มีข้อมูลจำนวนปศุสัตว์</Text>
                )}
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Flex>
    </Box>
  );
};

export default FarmerProfile;
